"use client";
import * as React from "react";
import { IconButton, Menu, MenuItem } from "@mui/material";
import { archiveProject, unarchiveProject, updateProject } from "../../api/projects";
import { PromptDialog } from "../Common/PromptDialog";
import { usePortalScope } from "@/analytics/PortalScope";

// Inline SVG so we don't take a hard dep on @mui/icons-material
// (not declared in chat-ui/package.json — only @mui/material is).
const MoreVertIcon = () => (
  <svg viewBox="0 0 24 24" width={20} height={20} fill="currentColor" aria-hidden="true">
    <path d="M12 8c1.1 0 2-.9 2-2s-.9-2-2-2-2 .9-2 2 .9 2 2 2zm0 2c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm0 6c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z"/>
  </svg>
);

interface Props {
  projectId: string;
  isArchived: boolean;
  currentName: string;
  onChanged: () => void;
}

export function ProjectActionsMenu({ projectId, isArchived, currentName, onChanged }: Props) {
  const [anchor, setAnchor] = React.useState<HTMLElement | null>(null);
  const [renameOpen, setRenameOpen] = React.useState(false);
  const menuScope = usePortalScope("actions");
  const renameDialogScope = usePortalScope("actions-rename_dialog")["data-ph-scope"];

  const submitRename = async (name: string) => {
    if (name !== currentName) {
      await updateProject(projectId, { name });
      onChanged();
    }
    setRenameOpen(false);
  };

  return (
    <>
      <IconButton data-ph="actions-open" onClick={(e) => setAnchor(e.currentTarget)} aria-label="More actions">
        <MoreVertIcon />
      </IconButton>
      <Menu anchorEl={anchor} open={Boolean(anchor)} onClose={() => setAnchor(null)} slotProps={{ list: menuScope }}>
        <MenuItem
          data-ph="rename"
          onClick={() => {
            setAnchor(null);
            setRenameOpen(true);
          }}
        >
          Rename
        </MenuItem>
        {/* No archive verb in the shared vocabulary yet, so archiving reads as a toggle to that state. */}
        {!isArchived ? (
          <MenuItem
            data-ph="toggle_archive"
            onClick={async () => {
              await archiveProject(projectId);
              setAnchor(null);
              onChanged();
            }}
          >
            Archive
          </MenuItem>
        ) : (
          <MenuItem
            data-ph="toggle_unarchive"
            onClick={async () => {
              await unarchiveProject(projectId);
              setAnchor(null);
              onChanged();
            }}
          >
            Unarchive
          </MenuItem>
        )}
      </Menu>
      <PromptDialog
        open={renameOpen}
        title="Rename project"
        label="Project name"
        initialValue={currentName}
        confirmLabel="Rename"
        onConfirm={submitRename}
        onClose={() => setRenameOpen(false)}
        scope={renameDialogScope}
      />
    </>
  );
}
